import React, { useState } from "react";
import { Box, LinearProgress, Stack, Typography } from "@mui/material";
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useQuery, useMutation } from "react-query";
import { Order } from "../../../types";
import AdminOrder from "../../../components/AdminOrder";
import AdminActionButton from "../../../components/AdminActionButton";

const fetchOrders = () => fetch(`${process.env.REACT_APP_BASE_URL}/admin/orders`, {
    method: 'GET',
    headers: {
        "x-api-key": process.env.REACT_APP_API_KEY || "",
        "Content-Type": "application/json",
    },
    redirect: 'follow'
})
    .then(response => response.json())
    .catch(error => console.log('error', error));

const markAsDelivered = (paymentIntentId: string) => fetch(`${process.env.REACT_APP_BASE_URL}/admin/orders/deliver`, {
    method: 'POST',
    headers: {
        "x-api-key": process.env.REACT_APP_API_KEY || "",
        "Content-Type": "application/json",
    },
    body: JSON.stringify({ paymentIntentId }),
    redirect: 'follow'
})
    .then(response => response.json())
    .catch(error => console.log('error', error));

const formatter = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
});

const AdminOrders = () => {
    const [selectedForDelivered, setSelectedForDelivered] = useState<string | undefined>();
    const [sortModel, setSortModel] = useState<GridSortModel>([{
        field: "dateShipped",
        sort: "desc"
    }]);

    const { data, isLoading, refetch } = useQuery(["fetchOrders"], fetchOrders, {
        onSuccess: () => setSelectedForDelivered(undefined)
    });
    const { mutate, isLoading: isMarkingDelivered } = useMutation(markAsDelivered, {
        onSuccess: () => {
            refetch();
        },
    });

    const handleMarkAsDelivered = (pId: string) => {
        setSelectedForDelivered(pId)
        mutate(pId);
    };

    const fetched = Boolean(data);
    const busy = isLoading || isMarkingDelivered;

    const shippedOrdersColumns: GridColDef[] = [
        { field: '_id', headerName: 'Order ID', width: 150 },
        { field: 'email', headerName: 'Customer Email', width: 200 },
        { field: 'amount', headerName: 'Amount Paid', width: 150, valueFormatter: (params) => formatter.format(params.value / 100) },
        { field: 'dateShipped', headerName: 'Shipped On', width: 150, valueFormatter: (params) => new Date(params.value)},
        { field: 'expectedDeliveryDate', headerName: 'Expected Delivery', width: 150 },
        { field: 'trackingNumber', headerName: 'Tracking Number', width: 200 },
        {
            field: 'action',
            headerName: 'Action',
            width: 150,
            sortable: false,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <AdminActionButton
                    loading={selectedForDelivered === params.row.paymentIntentId}
                    variant="contained"
                    color="primary"
                    size="small"
                    onClick={() => handleMarkAsDelivered(params.row.paymentIntentId)}
                >
                    Mark delivered
                </AdminActionButton>
            )
        },
    ];

    return (
        <Box>
            {busy && (
                <LinearProgress
                    sx={{
                        mb: 2,
                        borderRadius: 1,
                        position: "sticky",
                        top: 0,
                        zIndex: 2,
                    }}
                />
            )}

            <Stack spacing={4}>
            <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
                    New orders
                </Typography>
                <Stack spacing={2} sx={{ pl: { xs: 0, sm: 0 } }}>
                    {data?.newOrders?.map((order: Order) => (
                        <AdminOrder key={order._id} order={order} refetchOrders={refetch} />
                    ))}
                    {fetched && !data?.newOrders?.length && (
                        <Typography color="text.secondary">No new orders</Typography>
                    )}
                </Stack>
            </Box>

            <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
                    Shipped orders
                </Typography>
                {data?.shippedOrders?.length ? (
                    <Box height={400} sx={{ width: "100%" }}>
                        <DataGrid
                            rows={data.shippedOrders}
                            columns={shippedOrdersColumns}
                            getRowId={(row) => row._id}
                            paginationMode="client"
                            sortModel={sortModel}
                            onSortModelChange={(model) => setSortModel(model)}
                            disableRowSelectionOnClick
                            sx={{
                                border: 1,
                                borderColor: "divider",
                                borderRadius: 1,
                                "& .MuiDataGrid-columnHeaders": { bgcolor: "action.hover" },
                            }}
                        />
                    </Box>
                ) : fetched ? (
                    <Typography color="text.secondary">No orders in progress</Typography>
                ) : null}
            </Box>

            <Box>
                <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
                    Delivered orders
                </Typography>
                <Stack spacing={2}>
                    {data?.deliveredOrders?.map((order: Order) => (
                        <AdminOrder key={order._id} order={order} refetchOrders={refetch} />
                    ))}
                    {fetched && !data?.deliveredOrders?.length && (
                        <Typography color="text.secondary">No delivered orders yet</Typography>
                    )}
                </Stack>
            </Box>
            </Stack>
        </Box>
    )
}

export default AdminOrders;
